import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, BarChart, Shield } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-neutral-900">Cookie Policy</h1>
          <p className="text-neutral-600 mt-2">Last updated: January 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 space-y-8">
          {/* Introduction */}
          <section>
            <div className="flex items-center mb-4">
              <Cookie className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">What Are Cookies?</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed">
              Cookies are small text files that are placed on your device when you visit our
              website. They help us provide, protect, and improve our Finance BPA platform
              by remembering your preferences, analyzing usage patterns, and enhancing security.
            </p>
            <p className="text-neutral-700 leading-relaxed mt-4">
              This Cookie Policy explains how we use cookies and similar technologies, what types
              of cookies we use, and how you can manage your cookie preferences.
            </p>
          </section>

          {/* Types of Cookies */}
          <section>
            <div className="flex items-center mb-4">
              <Settings className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Types of Cookies We Use</h2>
            </div>
            <div className="space-y-6">
              {/* Essential Cookies */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Essential Cookies (Required)
                </h3>
                <p className="text-neutral-700 mb-2">
                  These cookies are necessary for the Service to function properly. They enable
                  core functionality such as:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>User authentication and session management</li>
                  <li>Security features and fraud prevention</li>
                  <li>Load balancing and performance optimization</li>
                  <li>Remembering your login status and preferences</li>
                </ul>
                <p className="text-neutral-600 text-sm mt-2 italic">
                  These cookies cannot be disabled as they are essential for the Service to work.
                </p>
              </div>

              {/* Functional Cookies */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Functional Cookies
                </h3>
                <p className="text-neutral-700 mb-2">
                  These cookies enhance functionality and personalization:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Remembering your language and regional preferences</li>
                  <li>Storing your dashboard layout and view preferences</li>
                  <li>Maintaining your workspace settings and filters</li>
                  <li>Enabling chat support and collaboration features</li>
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Analytics Cookies
                </h3>
                <p className="text-neutral-700 mb-2">
                  These cookies help us understand how you use our Service:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Page views and navigation patterns</li>
                  <li>Feature usage and engagement metrics</li>
                  <li>Error tracking and performance monitoring</li>
                  <li>User flow analysis to improve user experience</li>
                </ul>
                <p className="text-neutral-600 text-sm mt-2">
                  We use anonymized, aggregated data that does not identify individual users.
                </p>
              </div>

              {/* Performance Cookies */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Performance Cookies
                </h3>
                <p className="text-neutral-700 mb-2">
                  These cookies collect information about how you interact with our Service:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Page load times and responsiveness</li>
                  <li>Browser and device compatibility</li>
                  <li>Network performance and optimization</li>
                  <li>Error rates and technical issues</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section>
            <div className="flex items-center mb-4">
              <BarChart className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Third-Party Cookies</h2>
            </div>
            <p className="text-neutral-700 leading-relaxed mb-4">
              We may use trusted third-party services that set their own cookies. These include:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Analytics Services</h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>
                    <strong>Google Analytics:</strong> Website traffic and user behavior analysis
                  </li>
                  <li>
                    <strong>Mixpanel/Amplitude:</strong> Product analytics and feature usage
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Support Services</h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>
                    <strong>Intercom/Crisp:</strong> Customer support chat functionality
                  </li>
                  <li>
                    <strong>Zendesk:</strong> Help desk and ticket management
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Payment Processing</h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>
                    <strong>Stripe:</strong> Secure payment processing and fraud detection
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-neutral-700 leading-relaxed mt-4">
              These third parties have their own privacy policies and cookie practices. We
              encourage you to review their policies.
            </p>
          </section>

          {/* Cookie Duration */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Cookie Duration</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Session Cookies</h3>
                <p className="text-neutral-700">
                  Temporary cookies that are deleted when you close your browser. Used for
                  authentication and session management.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Persistent Cookies</h3>
                <p className="text-neutral-700">
                  Cookies that remain on your device for a set period (typically 30 days to 2
                  years) or until you delete them. Used for preferences and analytics.
                </p>
              </div>
            </div>
          </section>

          {/* Managing Cookies */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-neutral-900">Managing Your Cookie Preferences</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Browser Settings</h3>
                <p className="text-neutral-700 mb-2">
                  Most browsers allow you to control cookies through their settings. You can:
                </p>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>Block all cookies or only third-party cookies</li>
                  <li>Delete existing cookies</li>
                  <li>Set preferences to be notified when cookies are set</li>
                  <li>Configure cookies to be deleted when you close your browser</li>
                </ul>
                <p className="text-neutral-600 text-sm mt-2">
                  Note: Blocking essential cookies may prevent the Service from functioning
                  properly.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Browser-Specific Instructions
                </h3>
                <ul className="list-disc list-inside text-neutral-700 space-y-1 ml-4">
                  <li>
                    <strong>Chrome:</strong> Settings → Privacy and security → Cookies and other
                    site data
                  </li>
                  <li>
                    <strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data
                  </li>
                  <li>
                    <strong>Safari:</strong> Preferences → Privacy → Cookies and website data
                  </li>
                  <li>
                    <strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and
                    site permissions
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">Cookie Consent Banner</h3>
                <p className="text-neutral-700">
                  When you first visit our Service, you may see a cookie consent banner. You can
                  customize your preferences or accept/reject non-essential cookies. You can change
                  these preferences at any time through your account settings.
                </p>
              </div>
            </div>
          </section>

          {/* Do Not Track */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Do Not Track Signals</h2>
            <p className="text-neutral-700 leading-relaxed">
              Some browsers include a "Do Not Track" (DNT) feature that signals websites you visit
              that you do not want to have your online activity tracked. Currently, there is no
              standard for how DNT signals should be interpreted. We do not currently respond to
              DNT signals, but we respect your cookie preferences as described in this policy.
            </p>
          </section>

          {/* Financial Data and Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              Financial Data and Cookies
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              We want to assure you that cookies do not store your sensitive financial data. Your
              financial information (account numbers, transaction details, financial statements) is
              stored securely in our encrypted databases, not in cookies. Cookies are only used for
              technical functionality, preferences, and anonymized analytics.
            </p>
          </section>

          {/* Updates to Cookie Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              Updates to This Cookie Policy
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our
              practices or for legal, operational, or regulatory reasons. We will notify you of
              any material changes by posting the updated policy on this page and updating the
              "Last updated" date.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-neutral-50 rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">Contact Us</h2>
            <p className="text-neutral-700 leading-relaxed mb-4">
              If you have questions about our use of cookies or this Cookie Policy, please contact
              us:
            </p>
            <div className="space-y-2 text-neutral-700">
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@kreupai.com" className="text-primary-600 hover:underline">
                  privacy@kreupai.com
                </a>
              </p>
              <p>
                <strong>Data Protection Officer:</strong>{' '}
                <a href="mailto:dpo@kreupai.com" className="text-primary-600 hover:underline">
                  dpo@kreupai.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;

